import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	Button,
	AlertDialog,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogCancel,
	AlertDialogAction,
} from "@/components/primitives";
import terms from "./terms.md?raw";
import Markdown from "react-markdown";
import { computeVersion } from "./computeVersion";

type Mode = "view" | "consent";

interface TermsDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	mode?: Mode;
	onAccepted?: (version: string) => void;
	// Optional: pass preloaded content/version
	content?: string;
	version?: string;
}

export function TermsDialog({
	open,
	onOpenChange,
	mode = "view",
	onAccepted,
	content: providedContent,
	version: providedVersion,
}: TermsDialogProps) {
	const { updateProfile, signOut, profile } = useAuth();
	const [content, setContent] = useState<string>(providedContent || "");
	const [version, setVersion] = useState<string>(providedVersion || "");
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [updateError, setUpdateError] = useState<string | null>(null);
	const [updating, setUpdating] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const effectiveContent =
		providedContent && providedContent.length > 0 ? providedContent : content;
	const effectiveVersion =
		providedVersion && providedVersion.length > 0 ? providedVersion : version;

	useEffect(() => {
		async function load() {
			try {
				setLoading(true);
				setError(null);
				setUpdateError(null);
				console.log("computing");
				const v = await computeVersion(terms);
				console.log("computed version", v);
				if (v) {
					setVersion(v);
					setContent(terms);
				}
			} catch (e: any) {
				setError(String(e?.message || e));
			} finally {
				setLoading(false);
			}
		}
		if (open && !(providedContent && providedVersion)) {
			load();
		} else {
			setLoading(false);
		}
		// Clear update error when dialog opens
		if (open) {
			setUpdateError(null);
		}
	}, [providedContent, providedVersion, open]);

	const handleAgree = async () => {
		if (!effectiveVersion) return;
		setUpdating(true);
		setUpdateError(null);

		const { error } = await updateProfile({
			tnc_accepted: true,
			tnc_version: effectiveVersion,
			tnc_accepted_at: new Date().toISOString(),
		});

		if (error) {
			console.error("Error updating terms", error);
			const errorMessage =
				"message" in error
					? error.message
					: "Failed to update terms acceptance. Please try again.";
			setUpdateError(
				errorMessage || "Failed to update terms acceptance. Please try again.",
			);
			setUpdating(false);
			return; // Don't call onAccepted or close dialog on error
		}

		// Only proceed if update was successful
		onAccepted?.(effectiveVersion);
		onOpenChange(false);
	};

	const handleDisagree = () => {
		setConfirmOpen(true);
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (mode === "consent" && next === false) return; // block closing
				onOpenChange(next);
			}}
		>
			<DialogContent
				className="sm:max-w-2xl"
				hideClose={mode === "consent"}
				onEscapeKeyDown={(e) => {
					if (mode === "consent") e.preventDefault();
				}}
				onInteractOutside={(e) => {
					if (mode === "consent") e.preventDefault();
				}}
			>
				<DialogHeader>
					<DialogTitle>Terms & Conditions</DialogTitle>
					<DialogDescription>
						{mode === "consent"
							? "Please review the terms carefully."
							: "View the current terms and conditions."}
					</DialogDescription>
				</DialogHeader>
				<div className="border rounded-md h-[60vh] overflow-y-auto p-4 bg-muted/20">
					{loading && (
						<div className="text-sm text-gray-500">Loading terms...</div>
					)}
					{error && <div className="text-sm text-red-600">{error}</div>}
					{!loading && !error && (
						<div className="max-w-none space-y-3 text-sm leading-relaxed text-foreground">
							<Markdown>{effectiveContent}</Markdown>
						</div>
					)}
				</div>
				{updateError && (
					<div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
						<div className="text-sm text-red-600 dark:text-red-400">
							{updateError}
						</div>
					</div>
				)}
				<div className="flex items-center justify-between mt-4">
					<div className="text-xs text-gray-500">
						Version: {effectiveVersion || ""}
					</div>
					{mode === "consent" ? (
						<div className="flex gap-2">
							<Button
								variant="secondary"
								onClick={handleDisagree}
								disabled={updating}
							>
								Disagree
							</Button>
							<Button onClick={handleAgree} disabled={updating}>
								{updating ? "Saving..." : "Agree"}
							</Button>
						</div>
					) : (
						<div className="flex gap-2">
							<Button onClick={() => onOpenChange(false)}>Close</Button>
						</div>
					)}
				</div>
			</DialogContent>
			{/* Disagree confirmation */}
			<AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Are you sure?</AlertDialogTitle>
						<AlertDialogDescription>
							You must accept the Terms & Conditions to use Reflection Journal.
							If you continue, you will be signed out and can no longer access
							the app.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={() => setConfirmOpen(false)}>
							Go back
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={async () => {
								try {
									// Store user type before signing out
									const isMentor = profile?.user_type === "mentor";
									await signOut();
									// Redirect to appropriate login page
									window.location.href = isMentor ? "/mentor-login" : "/login";
								} catch (error) {
									console.error("Error signing out", error);
									// Fallback to regular login if error occurs
									window.location.href = "/login";
								}
							}}
						>
							I understand — Sign me out
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</Dialog>
	);
}
